import React, { useMemo, useState } from "react";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiBadge,
  EuiButton,
  EuiHorizontalRule,
  EuiTitle,
} from "@elastic/eui";
import {
  Download,
  BookOpen,
  HardDrive,
  Calendar,
  Monitor,
  Layers,
  Copy,
  Check,
  Shield,
  FileSearch,
  ClipboardCheck,
  Lock,
  Globe,
  Zap,
  Terminal,
  ChevronDown,
  Tag,
  type LucideIcon,
} from "lucide-react";
import { useAnketaI18n } from "src/internationalization";
import { url } from "src/api";
import { useAnketaAuth } from "src/auth";
import { useIsMobile } from "./appliance-fetaure";

interface AgentVersion {
  version: string;
  releaseDate: string;
  releaseNote: string;
}

const VERSIONS: AgentVersion[] = [
  {
    version: "4.14.5",
    releaseDate: "2026-05-01",
    releaseNote: "LTO build, stripped debug symbols, arm64 optimisations",
  },
  {
    version: "4.14.4",
    releaseDate: "2026-04-06",
    releaseNote: "LTO build, stripped debug symbols, arm64 optimisations",
  },
  {
    version: "4.12.0",
    releaseDate: "2026-03-19",
    releaseNote: "LTO build, stripped debug symbols, arm64 optimisations",
  },
];

const OPSANIO_DISPLAY_NAME = "opsanio Agent";

type OsId = "linux" | "windows" | "macos";

interface DownloadPackage {
  name: string;
  arch: string;
  filename: string;
  size: string;
  descKey: string;
  descDefault: string;
  signature: string;
  checksum: string;
  link: string;
  image: string;
}

function buildPackages(v: AgentVersion): Record<OsId, DownloadPackage[]> {
  const { version } = v;

  const checksums: Record<string, string> = {
    //v 4.14.5
    "4.14.5:deb":
      "9c6e0288024f65c6751d41a4f9bd9da37c176718444ce2c407b1299197a03eaf",
    "4.14.5:deb-arm64":
      "11966f1d2766e148ab2b1b9a4c7f1e275c0b4d84491f0889a7bfa0e8f815831f",
    "4.14.5:rpm":
      "3af7dcd8335843474470c1c51db67f6dcd62d1fc889e0168a6c9b540244d86a8",
    "4.14.5:rpm-arm64":
      "d3bfc1ff74e305feacbbe1f9e77bac5e0e65eea3c58c54af6f2651494be21483",
    "4.14.5:msi":
      "11fa39991286e042ebd8c154f4ea684d846122af7c48bcf8877b407db2e9c248",
    "4.14.5:macos-intel":
      "2898b5560522f718297ae57b55e8f4c198a1ca65b599630fc4426a415bf4de66",
    "4.14.5:macos-arm64":
      "df59cb19f1047d065beb2229b30741996f5136515c5abc31dbcffef24a195fdf",
    //v 4.14.4
    "4.14.4:deb":
      "5ddeb0f6ca40571a02f27953076a8b08724e0c6bf46d6def47b8f1b9240c5d87",
    "4.14.4:deb-arm64":
      "1d689a17bf1597711ca6cc173d24fd40eb770371bc1a8819096577b564adecd3",
    "4.14.4:rpm":
      "81facfda7f270ea16ed9ef24128078635dc36d2d1d16b3f4f49c728c75959df4",
    "4.14.4:rpm-arm64":
      "eceae935b54ffb77af6ac94e33c828fe3a7ff5b5b3dd6787006623d565bfc279",
    "4.14.4:msi":
      "f6318d975800e6f5e402628b4a5aeeb1827a53f7efc80bef5ede55f07ade561a",
    "4.14.4:macos-intel":
      "16126110cf19abed1f4823e3069e6de96b0be3631d6ae0cabc920de214b844e7",
    "4.14.4:macos-arm64":
      "2e7236ac55f38581956ac4978d019d2c295beb71e3f0667d06c99c8760c175a8",
    // v4.12.0
    "4.12.0:deb":
      "a2b1f8a19e125cec39243e2bc44e63d3e461b580f28814d4c07face873c118f8",
    "4.12.0:rpm":
      "5cf6f916066ccee2df7e242155f630dc3e48cb17844bbb97db1ba999ba1d1872",
    "4.12.0:msi":
      "5cccf7197ac4f92ec61ec689a1405d599b5e906ad96a36e86b6b858a033f50c2",
    "4.12.0:macos-intel":
      "1f211882d037d14c73b7098e5e674186a5cf7ca71a70f03e10d0d0c86753d76f",
    "4.12.0:macos-arm64":
      "5ef48a0a9ce8ba6e54b3c5d6d5ba2844f80353e09e6138785e22df83ade0b973",
  };

  const links: Record<string, string> = {
    //v 4.14.5
    "4.14.4:deb":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.5-1_amd64.deb",
    "4.14.4:deb-arm64":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.5-1_arm64.deb",
    "4.14.4:rpm":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.5-1_x86_64.rpm",
    "4.14.4:rpm-arm64":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.5-1_aarch64.rpm",
    "4.14.4:msi":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.5-2.msi",
    "4.14.4:macos-intel":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.5-1_intel64.pkg",
    "4.14.4:macos-arm64":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.5-1_arm64.pkg",
    //v 4.14.4
    "4.14.4:deb":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.4-11_amd64.deb",
    "4.14.4:deb-arm64":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.4-11_arm64.deb",
    "4.14.4:rpm":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.4-11_x86_64.rpm",
    "4.14.4:rpm-arm64":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.4-11_aarch64.rpm",
    "4.14.4:msi":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.4-11.msi",
    "4.14.4:macos-intel":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.4-11_intel64.pkg",
    "4.14.4:macos-arm64":
      "https://nexus.autonubil.net/repository/artifacts/opsanio/opsanio-agent/opsanio-agent_4.14.4-11_arm64.pkg",
    // v4.12.0
    "4.12.0:deb": "https://nextcloud.autonubil.net/s/f5R29936EgxZQ32/download",
    "4.12.0:rpm": "https://nextcloud.autonubil.net/s/p6qsEPGiid8GYYa/download",
    "4.12.0:msi": "https://nextcloud.autonubil.net/s/tNYsYCFbz397NPN/download",
    "4.12.0:macos-intel":
      "https://nextcloud.autonubil.net/s/a3MeMjMaiaf7G5T/download",
    "4.12.0:macos-arm64":
      "https://nextcloud.autonubil.net/s/q6sZkiBWGFtxYXp/download",
  };

  const cs = (key: string) => checksums[`${version}:${key}`] ?? "-";
  const lk = (key: string) => links[`${version}:${key}`] ?? "#";

  return {
    linux: [
      {
        name: "Debian / Ubuntu",
        arch: "amd64",
        filename: `opsanio-agent_${version}-11_amd64.deb`,
        size: "17.6 MB",
        descKey: "anketa.agent.download.pkg.debian.amd64",
        descDefault:
          "APT-compatible package for Debian-based distributions. Includes systemd service unit, auto-update hook and hardened config templates.",
        signature: "GPG Signed",
        image: "debian.svg",
        checksum: cs("deb"),
        link: lk("deb"),
      },
      {
        name: "Debian / Ubuntu",
        arch: "arm64",
        filename: `opsanio-agent_${version}-11_arm64.deb`,
        size: "11.0 MB",
        descKey: "anketa.agent.download.pkg.debian.amd64",
        descDefault:
          "APT-compatible package for Debian-based distributions. Includes systemd service unit, auto-update hook and hardened config templates.",
        signature: "GPG Signed",
        image: "debian.svg",
        checksum: cs("deb-arm64"),
        link: lk("deb-arm64"),
      },
      {
        name: "RHEL / CentOS / Fedora",
        arch: "x86_64",
        filename: `opsanio-agent_${version}-11_x86_64.rpm`,
        size: "40.8 MB",
        descKey: "anketa.agent.download.pkg.rhel",
        descDefault:
          "RPM with bundled SELinux policy. Supports RHEL 7/8/9, CentOS Stream and Rocky Linux.",
        signature: "GPG Signed",
        image: "redhat.svg",
        checksum: cs("rpm"),
        link: lk("rpm"),
      },
      {
        name: "RHEL / CentOS / Fedora",
        arch: "aarch64",
        filename: `opsanio-agent_${version}-11_aarch64.rpm`,
        size: "40.5 MB",
        descKey: "anketa.agent.download.pkg.rhel",
        descDefault:
          "RPM with bundled SELinux policy. Supports RHEL 7/8/9, CentOS Stream and Rocky Linux.",
        signature: "GPG Signed",
        image: "redhat.svg",
        checksum: cs("rpm-arm64"),
        link: lk("rpm-arm64"),
      },
    ],
    windows: [
      {
        name: "Windows Installer",
        arch: "x64",
        filename: `opsanio-agent_${version}-11.msi`,
        size: "30.8 MB",
        descKey: "anketa.agent.download.pkg.win.msi",
        descDefault:
          "MSI with setup wizard and silent-deploy support via GPO / SCCM. Compatible with Windows 10/11 and Server 2016-2025.",
        signature: "Authenticode",
        image: "windows.svg",
        checksum: cs("msi"),
        link: lk("msi"),
      },
    ],
    macos: [
      {
        name: "macOS (Intel)",
        arch: "intel64",
        filename: `opsanio-agent_${version}-11_intel64.pkg`,
        size: "11.4 MB",
        descKey: "anketa.agent.download.pkg.macos.intel",
        descDefault:
          "PKG installer for Intel-based Macs. Includes LaunchDaemon plist for automatic service start on boot.",
        signature: "Signed",
        image: "apple.svg",
        checksum: cs("macos-intel"),
        link: lk("macos-intel"),
      },
      {
        name: "macOS (Apple Silicon)",
        arch: "arm64",
        filename: `opsanio-agent_${version}-11_arm64.pkg`,
        size: "11.4 MB",
        descKey: "anketa.agent.download.pkg.macos.arm64",
        descDefault:
          "Native arm64 build for M1/M2/M3 Macs. Optimised for Apple Silicon - smaller overhead, faster startup.",
        signature: "Signed",
        image: "apple.svg",
        checksum: cs("macos-arm64"),
        link: lk("macos-arm64"),
      },
    ],
  };
}

const C = {
  accent: "var(--ak-accent)",
  accentLight: "var(--ak-accent-tint)",
  accentBorder: "var(--ak-accent-border)",
  success: "var(--ak-success)",
  successLight: "rgba(0,117,110,0.08)",
  successBorder: "rgba(0,117,110,0.2)",
  warning: "#8E5D00",
  warningLight: "rgba(142,93,0,0.08)",
  warningBorder: "rgba(142,93,0,0.25)",
  pageBg: "var(--ak-page-bg)",
  cardBg: "var(--ak-surface)",
  subtleBg: "var(--ak-surface-2)",
  border: "var(--ak-border)",
  borderHover: "var(--ak-accent-glow)",
  borderAccent: "var(--ak-accent-glow)",
  textPrimary: "var(--ak-text)",
  textSecondary: "var(--ak-text-secondary)",
  textMuted: "var(--ak-text-muted)",
  textOnAccent: "var(--ak-text-on-accent)",
  mono: '"JetBrains Mono","Fira Code",monospace',
  sans: "system-ui,-apple-system,sans-serif",
};

interface VersionPickerProps {
  versions: AgentVersion[];
  selected: AgentVersion;
  onSelect: (v: AgentVersion) => void;
  translate: ReturnType<typeof useAnketaI18n>["translate"];
}

const VersionPicker: React.FC<VersionPickerProps> = ({
  versions,
  selected,
  onSelect,
  translate,
}) => {
  const [open, setOpen] = useState(false);

  const t = (key: string, def: string) =>
    translate(key, { defaultMessage: def });

  const isLatest = (v: AgentVersion) => v.version === versions[0].version;

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 14px",
          borderRadius: 8,
          background: C.cardBg,
          border: `1px solid ${open ? C.borderHover : C.border}`,
          fontFamily: C.mono,
          fontSize: "0.82rem",
          fontWeight: 600,
          color: C.textPrimary,
          cursor: "pointer",
          boxShadow: open
            ? "0 4px 16px var(--ak-accent-tint-strong)"
            : "0 1px 4px var(--ak-shadow)",
          transition: "all 0.18s",
          minWidth: 220,
          justifyContent: "space-between",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <Tag size={14} color={C.accent} />
          <span style={{ color: C.accent }}>v{selected.version}</span>
          {isLatest(selected) && (
            <span
              style={{
                background: C.successLight,
                color: C.success,
                border: `1px solid ${C.successBorder}`,
                borderRadius: 100,
                fontSize: "0.62rem",
                fontWeight: 700,
                padding: "1px 7px",
                fontFamily: C.sans,
                letterSpacing: "0.03em",
              }}
            >
              {t("anketa.agent.version.latest", "LATEST")}
            </span>
          )}
        </span>
        <ChevronDown
          size={14}
          color={C.textMuted}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.18s",
          }}
        />
      </button>

      {open && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 99,
            }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              zIndex: 100,
              background: C.cardBg,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              boxShadow:
                "0 8px 32px var(--ak-shadow-strong), 0 2px 8px var(--ak-shadow)",
              minWidth: 300,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "8px 14px 6px",
                fontSize: "0.67rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: C.textMuted,
                fontFamily: C.sans,
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              {t("anketa.agent.version.picker.title", "Available Releases")}
            </div>

            {versions.map((v, i) => {
              const latest = i === 0;
              const prev = i === 1;
              const active = v.version === selected.version;
              return (
                <button
                  key={v.version}
                  onClick={() => {
                    onSelect(v);
                    setOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    width: "100%",
                    padding: "12px 14px",
                    background: active ? C.accentLight : "transparent",
                    border: "none",
                    borderBottom:
                      i < versions.length - 1
                        ? `1px solid ${C.border}`
                        : "none",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (!active)
                      (e.currentTarget as HTMLElement).style.background =
                        C.subtleBg;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = active
                      ? C.accentLight
                      : "transparent";
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: active
                        ? C.accent
                        : latest
                          ? C.success
                          : C.textMuted,
                      marginTop: 5,
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        marginBottom: 3,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: C.mono,
                          fontWeight: 700,
                          fontSize: "0.84rem",
                          color: active ? C.accent : C.textPrimary,
                        }}
                      >
                        v{v.version}
                      </span>
                      {latest && (
                        <span
                          style={{
                            background: C.successLight,
                            color: C.success,
                            border: `1px solid ${C.successBorder}`,
                            borderRadius: 100,
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            padding: "1px 7px",
                            fontFamily: C.sans,
                          }}
                        >
                          {t("anketa.agent.version.latest", "LATEST")}
                        </span>
                      )}
                      {prev && (
                        <span
                          style={{
                            background: C.warningLight,
                            color: C.warning,
                            border: `1px solid ${C.warningBorder}`,
                            borderRadius: 100,
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            padding: "1px 7px",
                            fontFamily: C.sans,
                          }}
                        >
                          {t("anketa.agent.version.previous", "PREVIOUS")}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "0.73rem",
                        color: C.textSecondary,
                        lineHeight: 1.5,
                        marginBottom: 3,
                      }}
                    >
                      {v.releaseNote}
                    </div>
                    <div
                      style={{
                        fontSize: "0.67rem",
                        color: C.textMuted,
                        fontFamily: C.mono,
                      }}
                    >
                      {v.releaseDate}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const TerminalPreview: React.FC<{ version: string }> = ({ version }) => (
  <div
    style={{
      background: "#1A1F2E",
      border: "1px solid rgba(0,0,0,0.15)",
      borderRadius: 12,
      overflow: "hidden",
      boxShadow:
        "0 8px 40px var(--ak-shadow-strong), 0 2px 8px var(--ak-shadow)",
      fontFamily: C.mono,
    }}
  >
    <style>{`@keyframes sgBlink{0%,100%{opacity:1}50%{opacity:0}}`}</style>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "11px 16px",
        background: "#252B3B",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      {(["#FF5F57", "#FFBC2E", "#28C840"] as string[]).map((c) => (
        <span
          key={c}
          style={{
            width: 11,
            height: 11,
            borderRadius: "50%",
            background: c,
            display: "inline-block",
          }}
        />
      ))}
      <span style={{ fontSize: "0.73rem", color: "#6B7897", marginLeft: 6 }}>
        opsanio-agent bash
      </span>
    </div>
    <div
      style={{
        padding: "22px",
        fontSize: "0.79rem",
        lineHeight: 2,
        color: "#A8B4CF",
      }}
    >
      <div>
        <span style={{ color: "#4ADE80" }}>root@host:~#</span>{" "}
        <span style={{ color: "#E2E8F4" }}>opsanio-agent --version</span>
      </div>
      <div style={{ color: "#60A5FA", fontWeight: 600 }}>
        {OPSANIO_DISPLAY_NAME} v{version}
      </div>
      <div style={{ marginTop: 8 }}>
        <span style={{ color: "#4ADE80" }}>root@host:~#</span>{" "}
        <span style={{ color: "#E2E8F4" }}>systemctl status opsanio-agent</span>
      </div>
      <div style={{ color: "#4ADE80" }}>● opsanio-agent.service</div>
      <div style={{ color: "#6B7897" }}>
        &nbsp;&nbsp;&nbsp;Loaded: loaded (/lib/systemd/system/…)
      </div>
      <div style={{ color: "#4ADE80" }}>
        &nbsp;&nbsp;&nbsp;Active: active (running) since{" "}
        {VERSIONS[0].releaseDate}
      </div>
      <div style={{ marginTop: 8 }}>
        <span style={{ color: "#4ADE80" }}>root@host:~#</span>{" "}
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 14,
            background: "#60A5FA",
            verticalAlign: "middle",
            borderRadius: 1,
            animation: "sgBlink 1.1s step-end infinite",
          }}
        />
      </div>
    </div>
  </div>
);

interface PackageCardProps {
  pkg: DownloadPackage;
  translate: ReturnType<typeof useAnketaI18n>["translate"];
  isAuthorized: boolean;
}

const PackageCard: React.FC<PackageCardProps> = ({
  pkg,
  translate,
  isAuthorized,
}) => {
  const [dlHover, setDlHover] = useState(false);
  const [copied, setCopied] = useState(false);

  const ext = pkg.filename.split(".").pop();

  const copyChecksum = () => {
    navigator.clipboard.writeText(pkg.checksum);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Only the download/copy controls are clickable, so only they react to
  // hover - a card-level hover would imply the whole card is a link.
  return (
    <div
      style={{
        background: C.cardBg,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 22,
        boxShadow: "0 1px 4px var(--ak-shadow)",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 14,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            background: C.accentLight,
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: C.accent,
          }}
        >
          <img src={`${url}v2/images/${pkg.image}`} alt={pkg.name} />
        </div>
        <span
          style={{
            fontFamily: C.mono,
            fontSize: "0.68rem",
            padding: "2px 9px",
            borderRadius: 100,
            background: C.subtleBg,
            color: C.textSecondary,
            border: `1px solid ${C.border}`,
          }}
        >
          {pkg.arch}
        </span>
      </div>

      <div
        style={{
          fontSize: "0.95rem",
          fontWeight: 700,
          color: C.textPrimary,
          marginBottom: 3,
        }}
      >
        {pkg.name}
      </div>
      <div
        style={{
          fontFamily: C.mono,
          fontSize: "0.73rem",
          color: C.accent,
          fontWeight: 600,
          marginBottom: 10,
        }}
      >
        .{ext}
      </div>

      <div
        style={{
          fontSize: "0.82rem",
          color: C.textSecondary,
          lineHeight: 1.65,
          marginBottom: 16,
          flex: 1,
        }}
      >
        {translate(pkg.descKey, { defaultMessage: pkg.descDefault })}
      </div>

      <div
        style={{
          display: "flex",
          gap: 14,
          flexWrap: "wrap",
          marginBottom: 16,
          paddingBottom: 14,
          borderBottom: isAuthorized ? `1px solid ${C.border}` : undefined,
        }}
      >
        {(
          [
            { Icon: HardDrive, label: pkg.size },
            {
              Icon: Calendar,
              label: pkg.filename.includes("opsanio") ? "see release" : "-",
            },
          ] as { Icon: LucideIcon; label: string }[]
        ).map(({ Icon, label }) => (
          <div
            key={label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: "0.71rem",
              color: C.textMuted,
            }}
          >
            <Icon size={12} /> {label}
          </div>
        ))}
      </div>

      <a href={pkg.link} download={pkg.filename}>
        <button
          onClick={() => {
            console.log("Debug", pkg);
          }}
          onMouseEnter={() => setDlHover(true)}
          onMouseLeave={() => setDlHover(false)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
            padding: "10px 14px",
            borderRadius: 7,
            background: dlHover ? C.accent : C.accentLight,
            color: dlHover ? C.textOnAccent : C.accent,
            border: `1px solid ${dlHover ? C.accent : C.accentBorder}`,
            fontFamily: C.sans,
            fontSize: "0.82rem",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.18s",
            marginBottom: 10,
            boxShadow: dlHover ? "0 2px 12px var(--ak-accent-glow)" : "none",
            overflow: "hidden",
          }}
        >
          <Download size={14} />
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {pkg.filename}
          </span>
        </button>
      </a>

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            fontFamily: C.mono,
            fontSize: "0.67rem",
            color: C.textMuted,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {pkg.checksum}
        </span>
        <button
          onClick={copyChecksum}
          title={translate(
            copied
              ? "anketa.agent.download.sha256.copied"
              : "anketa.agent.download.sha256.copy",
            { defaultMessage: copied ? "Copied!" : "Copy checksum" },
          )}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: copied ? C.success : C.textMuted,
            padding: 3,
            flexShrink: 0,
            transition: "color 0.18s",
            display: "flex",
            alignItems: "center",
          }}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
        </button>
      </div>
    </div>
  );
};

interface FeatureCardProps {
  Icon: LucideIcon;
  color: string;
  bg: string;
  title: string;
  desc: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  Icon,
  color,
  bg,
  title,
  desc,
}) => {
  // Not clickable -> deliberately no hover effect (a hover would imply a link).
  return (
    <div
      style={{
        padding: 24,
        borderRadius: 12,
        border: `1px solid ${C.border}`,
        background: C.cardBg,
        boxShadow: "0 1px 4px var(--ak-shadow)",
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 11,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: bg,
          color,
          marginBottom: 16,
        }}
      >
        <Icon size={21} />
      </div>
      <div
        style={{
          fontSize: "0.92rem",
          fontWeight: 700,
          color: C.textPrimary,
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: "0.82rem",
          color: C.textSecondary,
          lineHeight: 1.65,
        }}
      >
        {desc}
      </div>
    </div>
  );
};

const AgentDownloadSection: React.FC = () => {
  const { translate } = useAnketaI18n();
  const { keycloak } = useAnketaAuth();
  const isMobile = useIsMobile();

  const [activeOs, setActiveOs] = useState<OsId>("linux");
  const [selectedVersion, setSelectedVersion] = useState<AgentVersion>(
    VERSIONS[0],
  );

  const isLatestSelected = selectedVersion.version === VERSIONS[0].version;

  const isAuthorized = useMemo<boolean>(
    () => !!(keycloak && keycloak.authenticated),
    [keycloak],
  );

  const t = (key: string, defaultMessage: string) =>
    translate(key, { defaultMessage });

  const packages = useMemo(
    () => buildPackages(selectedVersion)[activeOs],
    [selectedVersion, activeOs],
  );

  const osTabs: {
    id: OsId;
    labelKey: string;
    labelDefault: string;
    Icon: LucideIcon;
  }[] = [
    {
      id: "linux",
      labelKey: "anketa.agent.download.tab.linux",
      labelDefault: "Linux",
      Icon: Terminal,
    },
    {
      id: "windows",
      labelKey: "anketa.agent.download.tab.windows",
      labelDefault: "Windows",
      Icon: Monitor,
    },
    {
      id: "macos",
      labelKey: "anketa.agent.download.tab.macos",
      labelDefault: "macOS",
      Icon: Layers,
    },
  ];

  const FEATURES: FeatureCardProps[] = [
    {
      Icon: Shield,
      color: C.accent,
      bg: C.accentLight,
      title: t(
        "anketa.agent.features.threat.title",
        "Threat Detection & Response",
      ),
      desc: t(
        "anketa.agent.features.threat.desc",
        "Correlates syscall events, file integrity changes and log patterns using 3,000+ signatures aligned with MITRE ATT&CK.",
      ),
    },
    {
      Icon: FileSearch,
      color: C.success,
      bg: C.successLight,
      title: t("anketa.agent.features.log.title", "Log Collection & Analysis"),
      desc: t(
        "anketa.agent.features.log.desc",
        "Collects, normalises and forwards logs from syslog, Windows Event Log, flat files and Docker - with zero-loss buffering.",
      ),
    },
    {
      Icon: ClipboardCheck,
      color: C.warning,
      bg: C.warningLight,
      title: t(
        "anketa.agent.features.compliance.title",
        "Compliance Automation",
      ),
      desc: t(
        "anketa.agent.features.compliance.desc",
        "PCI DSS, HIPAA, GDPR, CIS Benchmarks and NIST SP 800-53 checks out-of-the-box. Dashboards ready on first connect.",
      ),
    },
    {
      Icon: Lock,
      color: C.accent,
      bg: C.accentLight,
      title: t("anketa.agent.features.fim.title", "File Integrity Monitoring"),
      desc: t(
        "anketa.agent.features.fim.desc",
        "Detects unauthorised file and registry changes via cryptographic hashing. inotify on Linux for near-zero latency.",
      ),
    },
    {
      Icon: Globe,
      color: C.success,
      bg: C.successLight,
      title: t("anketa.agent.features.transport.title", "Encrypted Transport"),
      desc: t(
        "anketa.agent.features.transport.desc",
        "All agent-manager traffic over TLS 1.3 with mutual cert auth. Keys rotate automatically on every fresh deployment.",
      ),
    },
  ];

  return (
    <div
      id="opsanio-agent"
      style={{ background: C.pageBg, fontFamily: C.sans, color: C.textPrimary }}
    >
      <section
        style={{
          position: "relative",
          padding: "80px 48px 64px",
          overflow: "hidden",
          borderBottom: `1px solid ${C.border}`,
          background: `
            radial-gradient(ellipse 55% 50% at 65% -5%, var(--ak-accent-tint) 0%, transparent 65%),
            radial-gradient(ellipse 35% 35% at 15% 90%, rgba(0,117,110,0.05) 0%, transparent 60%),
            ${C.pageBg}`,
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <EuiFlexGroup gutterSize="xl" alignItems="center" wrap>
            <EuiFlexItem>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginBottom: 20,
                  flexWrap: "wrap",
                }}
              >
                <EuiBadge
                  style={{
                    background: C.accentLight,
                    color: C.accent,
                    border: `1px solid ${C.accentBorder}`,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                  }}
                >
                  v{VERSIONS[0].version}
                </EuiBadge>
                <EuiBadge
                  style={{
                    background: C.successLight,
                    color: C.success,
                    border: `1px solid ${C.successBorder}`,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                  }}
                >
                  {t("anketa.agent.hero.badge.stable", "✓ Stable Release")}
                </EuiBadge>
              </div>

              <EuiTitle size="l">
                <h1
                  style={{
                    color: C.textPrimary,
                    fontWeight: 800,
                    lineHeight: 1.15,
                    letterSpacing: "-0.02em",
                    marginBottom: 16,
                  }}
                >
                  {OPSANIO_DISPLAY_NAME}
                  <br />
                  <span
                    style={{
                      color: C.textMuted,
                      fontWeight: 300,
                      fontSize: "52%",
                    }}
                  >
                    {t(
                      "anketa.agent.hero.title.sub",
                      "Powered by Wazuh Recompiled",
                    )}
                  </span>
                </h1>
              </EuiTitle>

              <EuiText color="subdued">
                <p
                  style={{
                    fontSize: "1rem",
                    lineHeight: 1.75,
                    maxWidth: 500,
                    marginBottom: 32,
                  }}
                >
                  {t(
                    "anketa.agent.hero.description",
                    "A hardened, recompiled edition of the Wazuh endpoint agent. Real-time threat detection, compliance automation and deep log analysis - engineered for demanding enterprise environments with zero-touch deployment support.",
                  )}
                </p>
              </EuiText>

              <EuiFlexGroup gutterSize="m" wrap>
                <EuiFlexItem grow={false}>
                  <EuiButton
                    href="#opsanio-download"
                    fill
                    iconType={() => (
                      <Download size={15} style={{ marginRight: 4 }} />
                    )}
                  >
                    {t("anketa.agent.hero.cta.download", "Download Agent")}
                  </EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>

              <div
                style={{
                  display: "flex",
                  marginTop: 32,
                  background: C.cardBg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  overflow: "hidden",
                  boxShadow: "0 1px 4px var(--ak-shadow)",
                }}
              >
                {[
                  {
                    num: `v${VERSIONS[0].version}`,
                    labelKey: "anketa.agent.hero.stats.build",
                    labelDefault: "Latest Build",
                  },
                  {
                    num: "3",
                    labelKey: "anketa.agent.hero.stats.platforms",
                    labelDefault: "Platforms",
                  },
                  {
                    num: "GPLv2",
                    labelKey: "anketa.agent.hero.stats.license",
                    labelDefault: "License",
                  },
                  {
                    num: "<1s",
                    labelKey: "anketa.agent.hero.stats.latency",
                    labelDefault: "Alert Latency",
                  },
                ].map(({ num, labelKey, labelDefault }, i, arr) => (
                  <div
                    key={labelKey}
                    style={{
                      flex: 1,
                      padding: "16px 14px",
                      textAlign: "center",
                      borderRight:
                        i < arr.length - 1 ? `1px solid ${C.border}` : "none",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "1.3rem",
                        fontWeight: 800,
                        color: C.accent,
                        lineHeight: 1,
                      }}
                    >
                      {num}
                    </div>
                    <div
                      style={{
                        fontSize: "0.67rem",
                        color: C.textMuted,
                        marginTop: 4,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {t(labelKey, labelDefault)}
                    </div>
                  </div>
                ))}
              </div>
            </EuiFlexItem>

            <EuiFlexItem>
              <TerminalPreview version={VERSIONS[0].version} />
            </EuiFlexItem>
          </EuiFlexGroup>
        </div>
      </section>

      <div
        id="opsanio-download"
        style={{ maxWidth: 1200, margin: "0 auto", padding: "64px 48px" }}
      >
        <div
          style={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: C.accent,
            marginBottom: 10,
          }}
        >
          {t("anketa.agent.download.eyebrow", "Installation")}
        </div>
        <EuiTitle size="l">
          <h2
            style={{
              color: C.textPrimary,
              fontWeight: 800,
              letterSpacing: "-0.01em",
            }}
          >
            {t("anketa.agent.download.title", "Choose Your Platform")}
          </h2>
        </EuiTitle>
        <EuiText color="subdued">
          <p style={{ marginTop: 8, marginBottom: 28 }}>
            {t(
              "anketa.agent.download.subtitle",
              "All packages are signed and verified. Release date: " +
                selectedVersion.releaseDate +
                ".",
            )}
          </p>
        </EuiText>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 24,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              gap: 4,
              padding: 4,
              background: C.cardBg,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              boxShadow: "0 1px 3px var(--ak-shadow)",
            }}
          >
            {osTabs.map(({ id, labelKey, labelDefault, Icon }) => {
              const active = activeOs === id;
              return (
                <button
                  key={id}
                  onClick={() => setActiveOs(id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "8px 18px",
                    borderRadius: 5,
                    fontSize: "0.84rem",
                    fontWeight: 600,
                    fontFamily: C.sans,
                    cursor: "pointer",
                    color: active ? C.accent : C.textSecondary,
                    background: active ? C.accentLight : "transparent",
                    border: active
                      ? `1px solid ${C.accentBorder}`
                      : "1px solid transparent",
                    transition: "all 0.16s",
                  }}
                >
                  <Icon size={15} />
                  {t(labelKey, labelDefault)}
                </button>
              );
            })}
          </div>

          <VersionPicker
            versions={VERSIONS}
            selected={selectedVersion}
            onSelect={setSelectedVersion}
            translate={translate}
          />

          {!isLatestSelected && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "7px 14px",
                borderRadius: 8,
                background: C.warningLight,
                border: `1px solid ${C.warningBorder}`,
                fontSize: "0.78rem",
                color: C.warning,
                fontWeight: 600,
              }}
            >
              ⚠{" "}
              {t(
                "anketa.agent.version.old.warning",
                `You are viewing an older release (v{selectedVersion.version}). We recommend using the latest version.`,
              ).replace(
                "v{selectedVersion.version}",
                `v${selectedVersion.version}`,
              )}
            </div>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))",
            gap: 16,
          }}
        >
          {packages.map((pkg) => (
            <PackageCard
              key={pkg.filename}
              pkg={pkg}
              translate={translate}
              isAuthorized={isAuthorized}
            />
          ))}
        </div>

        {isAuthorized && (
          <div
            style={{
              marginTop: 24,
              padding: "16px 20px",
              background: C.cardBg,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "0 1px 4px var(--ak-shadow)",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  background: C.accentLight,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: C.accent,
                  flexShrink: 0,
                }}
              >
                <BookOpen size={17} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: "0.88rem",
                    fontWeight: 600,
                    color: C.textPrimary,
                  }}
                >
                  {t("anketa.agent.download.guide.title", "Installation Guide")}
                </div>
                <div
                  style={{
                    fontSize: "0.74rem",
                    color: C.textMuted,
                    fontFamily: C.mono,
                  }}
                >
                  opsanio-agent-installationshandbuch.docx 15 KB
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                /* wire download */
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 7,
                background: C.accentLight,
                color: C.accent,
                border: `1px solid ${C.accentBorder}`,
                fontFamily: C.sans,
                fontSize: "0.82rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.18s",
              }}
            >
              <Download size={14} />
              {t("anketa.agent.download.guide.btn", "Download")}
            </button>
          </div>
        )}
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 48px" }}>
        <EuiHorizontalRule margin="none" />
      </div>

      <section
        style={{ maxWidth: 1200, margin: "0 auto", padding: "64px 48px" }}
      >
        <div
          style={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: C.accent,
            marginBottom: 10,
          }}
        >
          {t("anketa.agent.features.eyebrow", "Capabilities")}
        </div>
        <EuiTitle size="l">
          <h2
            style={{
              color: C.textPrimary,
              fontWeight: 800,
              letterSpacing: "-0.01em",
            }}
          >
            {t(
              "anketa.agent.features.title",
              "Enterprise-grade security, simplified",
            )}
          </h2>
        </EuiTitle>
        <EuiText color="subdued">
          <p style={{ marginTop: 8, marginBottom: 40 }}>
            {t(
              "anketa.agent.features.subtitle",
              "Everything you expect from a Wazuh agent - hardened, patched and built for production.",
            )}
          </p>
        </EuiText>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fill, minmax(min(100%, 260px), 1fr))",
            gap: isMobile ? 12 : 20,
          }}
        >
          {FEATURES.map((f) => (
            <FeatureCard key={f.title} {...f} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default AgentDownloadSection;
